/**
 * 分時圈 × 公車站牌收割（由內向外、站牌不重複）
 * 舊版口徑：先 2 分鐘圈抓站 → 逐圈外擴；已收過的站不再收入外圈。
 * 路線則掛在「該站所屬分鐘」；同一路線只歸最早那一圈。
 */

import { cleanBusRouteRef } from './bus-route-display.js';
import {
    busRouteDetailsMatch,
    busRouteOperatorCode,
    busRouteStableIds,
    normalizeBusRouteToken
} from './bus-route-identity.js';
import { shouldIncludeInCityBusReport } from './tdx-bus-route-type.js';

export const ISOCHRONE_BUS_RING_SECONDS = [120, 240, 360, 480, 600, 720];

export function stopFeatureId(stop) {
    const p = stop?.properties || {};
    return String(p.stop_id || p.id || p.StopUID || '').trim() || null;
}

export function stopLonLat(stop) {
    const c = stop?.geometry?.coordinates;
    if (!Array.isArray(c) || c.length < 2) return null;
    const lon = Number(c[0]);
    const lat = Number(c[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return [lon, lat];
}

/** 穩定去重鍵：優先帶縣市前綴的穩定 ID，否則 縣市+清理後號碼 */
export function routeDetailKey(detail = {}) {
    const ids = [...busRouteStableIds(detail)];
    const strong = ids.find((id) => /^[A-Za-z]{3}/.test(String(id)) || (String(id).length >= 8 && /[A-Za-z]/.test(String(id))));
    if (strong) return String(strong).trim();
    const ref = cleanBusRouteRef(detail.ref || detail.h_ref || '')
        || String(detail.ref || detail.h_ref || '').trim();
    const city = busRouteOperatorCode(detail);
    const nameTok = normalizeBusRouteToken(detail.name || detail.h_route_name || detail.h_name || '');
    const key = [city, ref, nameTok].filter(Boolean).join('|');
    if (key) return key;
    // 禁止回傳裸號碼當唯一鍵（會跨縣撞）
    return city ? `${city}|${ref || ids[0] || ''}` : '';
}

export function routePillLabel(detail = {}) {
    const ref = cleanBusRouteRef(detail.ref || detail.h_ref || '')
        || String(detail.ref || '').trim();
    const name = String(detail.name || detail.h_route_name || detail.h_name || '').trim();
    if (name && ref && name !== ref) {
        if (name.startsWith(ref) || name.includes(ref)) return name;
        return `${ref} ${name}`.replace(/\s+/g, ' ').trim();
    }
    return name || ref || String(detail.route_id || '—');
}

/** 淺色分鐘底（黃／橙）用深字，否則白字 */
export function pillTextColor(bgHex) {
    const raw = String(bgHex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(raw)) return '#ffffff';
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.55 ? '#1a1a1a' : '#ffffff';
}

function linePartsFromGeom(geom) {
    if (!geom) return [];
    if (geom.type === 'LineString') {
        return Array.isArray(geom.coordinates) ? [geom.coordinates] : [];
    }
    if (geom.type === 'MultiLineString') {
        return Array.isArray(geom.coordinates) ? geom.coordinates : [];
    }
    return [];
}

/**
 * 只保留與分時圈相交的線段（丟掉飛到外縣的無關 MultiLine 碎片）。
 * 與圈相交的整段折線會保留（可能仍很長）；報表繪製須再經 clipRouteFeatureToExportBbox
 * 裁到「圈＋緩衝」以免 fitBounds 被 三重-內科／508 等長線撐爆。
 * turf 缺失時回 null（禁止回傳整條原線）。
 *
 * 注意：報表／畫面高亮請改用 {@link filterRouteFeatureToLatLngBoundsParts}（圈＋緩衝框），
 * 否則 MultiLine 中「在視窗內、但未碰多邊形」的銜接段會被丟掉 → 框內假斷線。
 */
export function filterRouteFeatureToIsochroneParts(feature, isoPolygonFeature, turfApi) {
    const T = turfApi || (typeof turf !== 'undefined' ? turf : null);
    if (!feature?.geometry || !isoPolygonFeature?.geometry || !T) return null;
    return filterLinePartsByIntersect(feature, isoPolygonFeature, T);
}

/**
 * 只保留與 LatLngBounds（報表裁切框，通常＝分時圈外 N km）相交的線段。
 * 比「只碰分時圈多邊形」寬：框內銜接段不會因略出圈外而被丟，避免假斷線。
 */
export function filterRouteFeatureToLatLngBoundsParts(feature, latLngBounds, turfApi) {
    const T = turfApi || (typeof turf !== 'undefined' ? turf : null);
    if (!feature?.geometry || !latLngBounds || !T?.bboxPolygon) return null;
    const w = latLngBounds.getWest?.();
    const s = latLngBounds.getSouth?.();
    const e = latLngBounds.getEast?.();
    const n = latLngBounds.getNorth?.();
    if (![w, s, e, n].every(Number.isFinite)) return null;
    let clipPoly;
    try {
        clipPoly = T.bboxPolygon([w, s, e, n]);
    } catch {
        return null;
    }
    return filterLinePartsByIntersect(feature, clipPoly, T);
}

function filterLinePartsByIntersect(feature, clipFeature, T) {
    const parts = linePartsFromGeom(feature.geometry);
    if (!parts.length) return null;
    const kept = [];
    for (const coords of parts) {
        if (!Array.isArray(coords) || coords.length < 2) continue;
        try {
            const line = T.lineString(coords);
            if (T.booleanIntersects(line, clipFeature)) kept.push(coords);
        } catch {
            /* 略過壞段 */
        }
    }
    if (!kept.length) return null;
    const geometry = kept.length === 1
        ? { type: 'LineString', coordinates: kept[0] }
        : { type: 'MultiLineString', coordinates: kept };
    return {
        type: 'Feature',
        properties: { ...(feature.properties || {}) },
        geometry
    };
}

/**
 * Leaflet LatLngBounds 四向外擴（公尺）。報表視窗／裁線緩衝用。
 */
export function expandLatLngBoundsMeters(latLngBounds, meters) {
    if (!latLngBounds?.isValid?.() || !(meters > 0)) return latLngBounds || null;
    const lat = latLngBounds.getCenter().lat;
    const dLat = meters / 111320;
    const cos = Math.cos((lat * Math.PI) / 180);
    const dLng = meters / (111320 * Math.max(0.2, cos));
    const Lref = typeof L !== 'undefined' ? L : null;
    const south = latLngBounds.getSouth() - dLat;
    const north = latLngBounds.getNorth() + dLat;
    const west = latLngBounds.getWest() - dLng;
    const east = latLngBounds.getEast() + dLng;
    if (Lref?.latLngBounds) {
        return Lref.latLngBounds([south, west], [north, east]);
    }
    return {
        isValid: () => true,
        getSouth: () => south,
        getNorth: () => north,
        getWest: () => west,
        getEast: () => east,
        getCenter: () => ({ lat: (south + north) / 2, lng: (west + east) / 2 })
    };
}

/**
 * 銷售稿／匯出取景：舊規則自然框為準，四邊各自以中心為基準設上限。
 * - 單邊自中心延伸 ≤ maxKm → 用自然邊（外圈∪碰到圈的路線）
 * - 超過 → 該邊截到 maxKm
 * 四邊獨立，互不強迫對稱。
 *
 * @param {[number, number, number, number]} naturalBbox [west, south, east, north]
 * @param {[number, number]|{lng:number,lat:number}|{lon:number,lat:number}} centerLngLat
 * @param {number} [maxKm=5]
 * @returns {{
 *   bbox: [number, number, number, number],
 *   capped: { west: boolean, east: boolean, south: boolean, north: boolean },
 *   maxKm: number
 * }}
 */
export function capFrameSidesByCenterKm(naturalBbox, centerLngLat, maxKm = 5) {
    const [nw, ns, ne, nn] = Array.isArray(naturalBbox) ? naturalBbox : [];
    if (![nw, ns, ne, nn].every(Number.isFinite)) {
        return {
            bbox: [nw, ns, ne, nn],
            capped: { west: false, east: false, south: false, north: false },
            maxKm
        };
    }
    let lng;
    let lat;
    if (Array.isArray(centerLngLat)) {
        lng = Number(centerLngLat[0]);
        lat = Number(centerLngLat[1]);
    } else if (centerLngLat && typeof centerLngLat === 'object') {
        lng = Number(centerLngLat.lng ?? centerLngLat.lon);
        lat = Number(centerLngLat.lat);
    }
    const lim = Number(maxKm);
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || !(lim > 0)) {
        return {
            bbox: [nw, ns, ne, nn],
            capped: { west: false, east: false, south: false, north: false },
            maxKm: lim
        };
    }
    const dLat = lim / 111.32;
    const cos = Math.cos((lat * Math.PI) / 180);
    const dLng = lim / (111.32 * Math.max(0.2, cos));
    const westCap = lng - dLng;
    const eastCap = lng + dLng;
    const southCap = lat - dLat;
    const northCap = lat + dLat;
    const capped = {
        west: nw < westCap,
        east: ne > eastCap,
        south: ns < southCap,
        north: nn > northCap
    };
    return {
        bbox: [
            capped.west ? westCap : nw,
            capped.south ? southCap : ns,
            capped.east ? eastCap : ne,
            capped.north ? northCap : nn
        ],
        capped,
        maxKm: lim
    };
}

/**
 * 報表繪製：把路線裁到分時圈 bbox（可微 pad），避免長線在圖上像「另一個比例尺」。
 * @param {{ getWest: Function, getSouth: Function, getEast: Function, getNorth: Function }} latLngBounds Leaflet LatLngBounds
 * @param {number} [padRatio=0.12] 相對長寬的外擴比例（已用 expandLatLngBoundsMeters 時可傳 0）
 */
export function clipRouteFeatureToExportBbox(feature, latLngBounds, turfApi, padRatio = 0.12) {
    const T = turfApi || (typeof turf !== 'undefined' ? turf : null);
    if (!feature?.geometry || !latLngBounds || !T?.bboxClip) return feature || null;
    const w = latLngBounds.getWest();
    const s = latLngBounds.getSouth();
    const e = latLngBounds.getEast();
    const n = latLngBounds.getNorth();
    if (![w, s, e, n].every(Number.isFinite)) return feature;
    const padX = Math.max(1e-5, Math.abs(e - w) * padRatio);
    const padY = Math.max(1e-5, Math.abs(n - s) * padRatio);
    const bbox = [w - padX, s - padY, e + padX, n + padY];
    try {
        const clipped = T.bboxClip(feature, bbox);
        if (!clipped?.geometry) return null;
        const parts = linePartsFromGeom(clipped.geometry);
        if (!parts.length) return null;
        return {
            type: 'Feature',
            properties: { ...(feature.properties || {}) },
            geometry: clipped.geometry
        };
    } catch {
        return feature;
    }
}

/**
 * @param {object} opts
 * @param {Array<{ seconds: number, minutes: number, color: string, feature: object }>} opts.ringsAsc 由小到大
 * @param {object[]} opts.stops GeoJSON Point features
 * @param {(lon:number, lat:number, polygonFeature:object) => boolean} opts.pointInPolygon
 * @param {(stop:object) => object[]} opts.routesForStop
 * @returns {{
 *   rings: Array<{ seconds, minutes, color, stops, routes }>,
 *   allStops: object[],
 *   routeAssignments: Array<{ key, label, seconds, minutes, color, detail }>
 * }}
 */
export function harvestBusStopsByIsochroneRings(opts = {}) {
    const {
        ringsAsc = [],
        stops = [],
        pointInPolygon,
        routesForStop
    } = opts;

    const seenStops = new Set();
    const seenRouteKeys = new Set();
    const seenLabels = new Set();
    const seenDetails = [];
    const rings = [];
    const allStops = [];
    const routeAssignments = [];

    function alreadySeenRoute(detail) {
        const key = routeDetailKey(detail);
        if (key && seenRouteKeys.has(key)) return true;
        for (const id of busRouteStableIds(detail)) {
            if (id && seenRouteKeys.has(String(id))) return true;
        }
        for (const prev of seenDetails) {
            if (busRouteDetailsMatch(prev, detail)) return true;
        }
        // 標籤去重須帶縣市，禁止裸「705」擋住／混進外縣同號
        const lab = normalizeBusRouteToken(routePillLabel(detail));
        const city = busRouteOperatorCode(detail);
        const labKey = city && lab ? `${city}|${lab}` : '';
        if (labKey && seenLabels.has(labKey)) return true;
        return false;
    }

    function markSeenRoute(detail, key, label) {
        if (key) seenRouteKeys.add(key);
        for (const id of busRouteStableIds(detail)) {
            if (id) seenRouteKeys.add(String(id));
        }
        const lab = normalizeBusRouteToken(label);
        const city = busRouteOperatorCode(detail);
        const labKey = city && lab ? `${city}|${lab}` : '';
        if (labKey) seenLabels.add(labKey);
        seenDetails.push(detail);
    }

    for (const ring of ringsAsc) {
        const ringStops = [];
        const ringRoutes = [];

        for (const stop of stops) {
            const sid = stopFeatureId(stop);
            if (!sid || seenStops.has(sid)) continue;
            const ll = stopLonLat(stop);
            if (!ll) continue;
            let inside = false;
            try {
                inside = !!pointInPolygon(ll[0], ll[1], ring.feature);
            } catch {
                inside = false;
            }
            if (!inside) continue;

            seenStops.add(sid);
            ringStops.push(stop);
            allStops.push(stop);

            const details = typeof routesForStop === 'function' ? (routesForStop(stop) || []) : [];
            for (const detail of details) {
                if (!shouldIncludeInCityBusReport(detail)) continue;
                if (alreadySeenRoute(detail)) continue;
                const key = routeDetailKey(detail);
                if (!key) continue;
                const label = routePillLabel(detail);
                markSeenRoute(detail, key, label);
                const row = {
                    key,
                    label,
                    seconds: ring.seconds,
                    minutes: ring.minutes,
                    color: ring.color,
                    detail
                };
                ringRoutes.push(row);
                routeAssignments.push(row);
            }
        }

        rings.push({
            seconds: ring.seconds,
            minutes: ring.minutes,
            color: ring.color,
            stops: ringStops,
            routes: ringRoutes
        });
    }

    return { rings, allStops, routeAssignments };
}
